import axios from 'axios'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuthContext } from '../context/AuthContext'
import type { AccountDeletionResponse } from '../services/auth'
import { submitAccountDeletion } from '../services/auth'

const ACCOUNT_DELETION_STORAGE_KEY = 'vault.accountDeletionResult'

const normalizeEmail = (value: string): string => value.trim().toLowerCase()

const loadStoredDeletionResult = (): AccountDeletionResponse | null => {
  if (typeof window === 'undefined') {
    return null
  }

  const raw = window.sessionStorage.getItem(ACCOUNT_DELETION_STORAGE_KEY)
  if (!raw) {
    return null
  }

  try {
    return JSON.parse(raw) as AccountDeletionResponse
  } catch {
    window.sessionStorage.removeItem(ACCOUNT_DELETION_STORAGE_KEY)
    return null
  }
}

const formatTimestamp = (value: string): string =>
  new Date(value).toLocaleString(undefined, {
    dateStyle: 'long',
    timeStyle: 'short'
  })

function AccountSettingsPage() {
  const { user, isAuthenticated, isLoading, beginGoogleLogin, refresh } = useAuthContext()
  const [confirmationEmail, setConfirmationEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle')
  const [deletionResult, setDeletionResult] = useState<AccountDeletionResponse | null>(() => loadStoredDeletionResult())

  useEffect(() => {
    if (isAuthenticated) {
      window.sessionStorage.removeItem(ACCOUNT_DELETION_STORAGE_KEY)
      setDeletionResult(null)
    }
  }, [isAuthenticated])

  const emailMatches = normalizeEmail(confirmationEmail) === normalizeEmail(user?.email ?? '')

  const handleCopyRequestId = async (): Promise<void> => {
    if (!deletionResult?.request_id) {
      return
    }

    try {
      await navigator.clipboard.writeText(deletionResult.request_id)
      setCopyState('copied')
    } catch {
      setCopyState('failed')
    }
  }

  const handleSubmit = async (): Promise<void> => {
    if (!user || !emailMatches || isSubmitting) {
      return
    }

    const confirmed = window.confirm(
      'Deleting your account signs you out immediately, disconnects Plaid, and schedules final purge after 30 days. Continue?'
    )
    if (!confirmed) {
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      const response = await submitAccountDeletion(confirmationEmail)
      window.sessionStorage.setItem(ACCOUNT_DELETION_STORAGE_KEY, JSON.stringify(response))
      setDeletionResult(response)
      setConfirmationEmail('')
      await refresh()
    } catch (err) {
      if (axios.isAxiosError(err)) {
        const message = (err.response?.data as { error?: string } | undefined)?.error
        setError(message ?? 'Unable to schedule account deletion right now.')
      } else {
        setError('Unable to schedule account deletion right now.')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  if (deletionResult) {
    const emailDeliveryFailed = deletionResult.email_delivery === 'failed'

    return (
      <div className="legal-page">
        <div className="legal-hero">
          <p className="legal-kicker">Account Settings</p>
          <h1>Deletion Scheduled</h1>
          <p className="legal-subtitle">
            Your account is locked, active sessions were revoked, and Plaid connections were disconnected.
          </p>
        </div>

        <div className={`legal-card account-deletion-status ${emailDeliveryFailed ? 'danger' : 'success'}`}>
          <h2>{emailDeliveryFailed ? 'Cancellation email failed' : 'Check your inbox'}</h2>
          <p>
            Request ID <strong>{deletionResult.request_id}</strong> is scheduled for permanent purge on{' '}
            <strong>{formatTimestamp(deletionResult.scheduled_purge_at)}</strong>.
          </p>
          <p>
            {emailDeliveryFailed
              ? `We could not send your cancellation email. To stop this deletion, contact ${deletionResult.support_email} within 30 days with the request ID above.`
              : 'We emailed you a signed cancellation link. If you cancel the deletion, you will need to sign in again and re-link your institutions.'}
          </p>
          <div className="account-deletion-actions">
            <button className="btn btn-secondary" onClick={() => { void handleCopyRequestId() }}>
              Copy Request ID
            </button>
            <a className="btn btn-secondary" href={`mailto:${deletionResult.support_email}`}>
              Contact Support
            </a>
          </div>
          {copyState === 'copied' && <p className="account-copy-state">Request ID copied.</p>}
          {copyState === 'failed' && <p className="account-copy-state">Copy failed. Use the request ID shown above.</p>}
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return (
      <div className="home-page">
        <h1 className="home-title">Account Settings</h1>
        <p className="home-subtitle">
          Sign in to manage your account or submit an account deletion request.
        </p>
        {!isLoading && (
          <button
            onClick={() => { void beginGoogleLogin(window.location.href) }}
            className="btn btn-primary"
          >
            Sign in with Google
          </button>
        )}
      </div>
    )
  }

  return (
    <div className="legal-page">
      <div className="legal-hero">
        <p className="legal-kicker">Account Settings</p>
        <h1>Manage Your Account</h1>
        <p className="legal-subtitle">
          Review your account details, support options, and the permanent account deletion flow.
        </p>
      </div>

      <div className="legal-grid">
        <section className="legal-card">
          <h2>Identity</h2>
          <p>{user.email}</p>
          <p>{[user.first_name, user.last_name].filter(Boolean).join(' ') || 'Name not provided'}</p>
        </section>

        <section className="legal-card">
          <h2>Support</h2>
          <p>
            Need help instead of deleting your account? Email{' '}
            <a href="mailto:support@vaultfinance.ai">support@vaultfinance.ai</a>.
          </p>
        </section>

        <section className="legal-card">
          <h2>Deletion Policy</h2>
          <p>
            Deletion locks the account immediately, disconnects Plaid immediately, and schedules final purge after 30 days.
          </p>
        </section>
      </div>

      <section className="legal-card account-danger-zone">
        <h2>Delete Account</h2>
        <p>
          This permanently deletes your Vault account after a 30-day grace window. If you cancel during that window,
          you will need to sign in again and re-link your institutions.
        </p>

        <div className="form-group">
          <label htmlFor="confirmation-email">Type your account email to continue</label>
          <input
            id="confirmation-email"
            type="email"
            value={confirmationEmail}
            onChange={(event) => setConfirmationEmail(event.target.value)}
            placeholder={user.email}
            autoComplete="email"
          />
        </div>

        <p className="account-hint">
          The address must match <strong>{user.email}</strong> exactly.
        </p>

        {error && <div className="error-banner">{error}</div>}

        <div className="account-deletion-actions">
          <button
            className="btn btn-danger"
            disabled={!emailMatches || isSubmitting}
            onClick={() => { void handleSubmit() }}
          >
            {isSubmitting ? 'Scheduling deletion…' : 'Delete Account'}
          </button>
          <Link to="/support" className="btn btn-secondary">
            Contact Support
          </Link>
        </div>
      </section>
    </div>
  )
}

export default AccountSettingsPage
